// Session storage for the dashboard.
//
// DELIBERATE TRADEOFF, do not rediscover as a bug: the token lives in
// localStorage, which any XSS can read. The alternative (httpOnly cookie) is
// cross-site here — dashboard on Netlify, API on Fly — where SameSite=None
// cookies need CSRF defences and Safari intermittently drops them. Chosen
// compensating controls: a strict CSP (netlify.toml), zero third-party JS,
// 7-day server-revocable sessions, and the policy engine still capping writes.
export type SessionAccount = {
  id: string;                              // the account (tenant) id
  email: string;                           // the signed-in PERSON's email
  role: "customer" | "operator";           // the tenant's role
  member_role?: "owner" | "member";        // the person's role in the account
  name?: string | null;                    // the person's display name
  account_name?: string;                   // the tenant's display name
  platform_admin?: boolean;                // the PERSON is a platform admin
};

export type MembershipSummary = {
  id: string;
  name: string;
  role: "customer" | "operator";
  member_role: "owner" | "member";
};

export type Session = { token: string; account: SessionAccount };

const KEY = "fk_session";
const ADMIN_KEY = "fk_session_admin";
const listeners = new Set<() => void>();

export function getSession(): Session | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Session;
    return parsed.token && parsed.account ? parsed : null;
  } catch {
    return null;
  }
}

export function setSession(s: Session): void {
  localStorage.setItem(KEY, JSON.stringify(s));
  emit();
}

/** Patch the account fields on the stored session (e.g. a renamed display
 *  name) without re-issuing the token. No-op when signed out. */
export function updateSession(patch: Partial<SessionAccount>): void {
  const cur = getSession();
  if (!cur) return;
  localStorage.setItem(KEY, JSON.stringify({ ...cur, account: { ...cur.account, ...patch } }));
  emit();
}

export function clearSession(): void {
  localStorage.removeItem(KEY);
  localStorage.removeItem(ADMIN_KEY);
  emit();
}

/** Stash the current (admin) session and swap in a read-only support session. */
export function startImpersonation(next: Session): void {
  const cur = getSession();
  if (cur) localStorage.setItem(ADMIN_KEY, JSON.stringify(cur));
  localStorage.setItem(KEY, JSON.stringify(next));
  emit();
}

/** Restore the stashed admin session. Returns it, or null if none was stashed. */
export function endImpersonation(): Session | null {
  try {
    const raw = localStorage.getItem(ADMIN_KEY);
    localStorage.removeItem(ADMIN_KEY);
    if (!raw) {
      emit();
      return null;
    }
    const parsed = JSON.parse(raw) as Session;
    if (!parsed.token || !parsed.account) {
      emit();
      return null;
    }
    localStorage.setItem(KEY, JSON.stringify(parsed));
    emit();
    return parsed;
  } catch {
    localStorage.removeItem(ADMIN_KEY);
    emit();
    return null;
  }
}

export function isImpersonating(): boolean {
  return localStorage.getItem(ADMIN_KEY) !== null;
}

export function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY || e.key === ADMIN_KEY) cb();
  };
  addEventListener("storage", onStorage);
  return () => {
    listeners.delete(cb);
    removeEventListener("storage", onStorage);
  };
}

function emit(): void {
  for (const cb of listeners) cb();
}
